import React, { useEffect } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useMutation } from "@tanstack/react-query";
import { useForm } from "react-hook-form";
import { toast } from "react-hot-toast";

import FormField from "@mhj/shared/FormField";
import { apiRequest } from "@mhj/shared/api";

function useQueryParam(name: string) {
  const { search } = useLocation();
  const params = new URLSearchParams(search);
  return params.get(name);
}

type ActivateForm = {
  token: string;
  password: string;
  confirmPassword: string;
};

export default function ActivateAccount() {
  const navigate = useNavigate();
  const tokenFromUrl = useQueryParam("token") ?? "";
  const {
    register,
    handleSubmit,
    setValue,
    formState: { errors },
  } = useForm<ActivateForm>({
    defaultValues: {
      token: tokenFromUrl,
      password: "",
      confirmPassword: "",
    },
  });

  const activateMutation = useMutation({
    mutationFn: (payload: { token: string; password: string }) =>
      apiRequest("/auth/activate-admin", {
        method: "POST",
        data: payload,
      }),
    onSuccess: () => {
      toast.success("Account activated. You can now sign in.");
      navigate("/login", { replace: true });
    },
  });

  useEffect(() => {
    setValue("token", tokenFromUrl);
  }, [tokenFromUrl, setValue]);

  const onSubmit = async (values: ActivateForm) => {
    if (values.password !== values.confirmPassword) {
      toast.error("Passwords do not match.");
      return;
    }
    try {
      await activateMutation.mutateAsync({
        token: values.token.trim(),
        password: values.password,
      });
    } catch (err) {
      toast.error((err as Error).message);
    }
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-sky-50 px-6">
      <form
        onSubmit={handleSubmit(onSubmit)}
        className="glass-card w-full max-w-md rounded-3xl p-8"
      >
        <p className="text-xs uppercase tracking-[0.3em] text-blue-300">
          Account activation
        </p>
        <h1 className="mt-3 text-2xl font-semibold text-slate-900">
          Set your admin password
        </h1>
        <p className="mt-2 text-sm text-slate-500">
          Use the token from your email invite to activate your admin account.
        </p>
        <div className="mt-6 space-y-4">
          <FormField label="Activation token">
            <input
              type="text"
              {...register("token", { required: "Token is required" })}
              className="rounded-2xl border border-blue-200 bg-white px-4 py-3 text-slate-900 placeholder:text-slate-400 focus:border-blue-500 focus:outline-none"
              placeholder="Paste token"
            />
          </FormField>
          {errors.token ? (
            <p className="text-xs text-red-500">{errors.token.message}</p>
          ) : null}
          <FormField label="Password">
            <input
              type="password"
              {...register("password", {
                required: "Password is required",
                minLength: {
                  value: 8,
                  message: "Password must be at least 8 characters",
                },
              })}
              className="rounded-2xl border border-blue-200 bg-white px-4 py-3 text-slate-900 placeholder:text-slate-400 focus:border-blue-500 focus:outline-none"
              placeholder="At least 8 characters"
            />
          </FormField>
          {errors.password ? (
            <p className="text-xs text-red-500">{errors.password.message}</p>
          ) : null}
          <FormField label="Confirm password">
            <input
              type="password"
              {...register("confirmPassword", {
                required: "Please confirm your password",
              })}
              className="rounded-2xl border border-blue-200 bg-white px-4 py-3 text-slate-900 placeholder:text-slate-400 focus:border-blue-500 focus:outline-none"
              placeholder="Repeat your password"
            />
          </FormField>
          {errors.confirmPassword ? (
            <p className="text-xs text-red-500">{errors.confirmPassword.message}</p>
          ) : null}
        </div>
        <button
          type="submit"
          disabled={activateMutation.isPending}
          className="mt-6 w-full rounded-full bg-blue-600 px-4 py-3 text-xs font-semibold uppercase tracking-[0.3em] text-white transition hover:bg-blue-500 disabled:cursor-not-allowed disabled:bg-blue-200"
        >
          {activateMutation.isPending ? "Activating..." : "Activate account"}
        </button>
        <div className="mt-6 text-center text-xs text-slate-500">
          Already activated?{" "}
          <Link to="/login" className="text-blue-600 hover:text-blue-700">
            Sign in
          </Link>
        </div>
      </form>
    </div>
  );
}
